import React, { useState } from 'react';
import {
    Dialog,
    DialogActions,
    Button,
    DialogTitle,
    DialogContent,
    Typography,
    Divider,
    FormControl,
    InputLabel,
    FilledInput,
    InputAdornment,
    IconButton,
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { useSelector, useDispatch } from 'react-redux';
import { ChangePasswordDialogProgress } from '../../types';
import ErrorIcon from '@mui/icons-material/Error';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useAuthHelper, useAuthState, getUserId } from '../AuthContextProvider';
import { useHistory } from 'react-router-dom';
import { Thunks, Actions } from '../../actions';
import { UserSelectors } from '../../selectors/user-selectors';
import { Selectors } from '../../selectors';
import { SetPasswordControl } from '../SetPasswordControl/SetPasswordControl';

const useStyles = makeStyles(() => ({
    largeIcon: {
        width: 60,
        height: 60,
    },
}));
type DialogProps = {
    show: boolean;
    closeDialog: Function;
};

export const ChangePasswordDialog: React.FC<DialogProps> = (props) => {
    const classes = useStyles();
    const { t } = useLanguageLocale();
    const dispatch = useDispatch();
    const authHelper = useAuthHelper();
    const authState = useAuthState();
    const history = useHistory();
    const changePassword = useSelector(Selectors.User.changePassword);
    const [showPassword, setShowPassword] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');

    const [passwordValid, setPasswordsValid] = useState(false);
    const userEmail = useSelector(UserSelectors.email);
    const userId = getUserId(authState);

    const Done = (): void => {
        dispatch(Thunks.UserSettings.userChangePassword(oldPassword, newPassword, authHelper));
    };

    const handleClose = (): void => {
        props.closeDialog();
        setOldPassword('');
    };

    // Reset the dialog state (before it renders), but only when the component is first mounted
    React.useLayoutEffect(() => {
        dispatch(Actions.UserSettings.resetChangePasswordSettings());
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return changePassword.progress === ChangePasswordDialogProgress.Succeeded ? (
        
            <Dialog
                disableEscapeKeyDown={true}
                open={props.show}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
                maxWidth="xs">
                <DialogTitle id="dialog-success" style={{ display: 'flex', justifyContent: 'center' }}>
                    <AccountCircleIcon className={classes.largeIcon} color={'primary'}></AccountCircleIcon>
                </DialogTitle>
                <DialogContent style={{ marginBottom: 20 }}>
                    <Typography variant="h5" style={{ marginBottom: 20 }}>
                        {t('CHANGE_PASSWORD.PASSWORD_CHANGED')}
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: 20 }} id="success-Title">
                        {t('CHANGE_PASSWORD.SUCCESS_MESSAGE')}
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: 150 }}>
                        {t('CHANGE_PASSWORD.EMAIL_CONFIRM_MESSAGE', { replace: { email: userEmail } })}
                    </Typography>
                </DialogContent>
                <Divider style={{ marginBottom: 50 }}></Divider>
                <DialogActions>
                    <Button
                        id="btnLogin"
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={(): void => {
                            dispatch(Thunks.Authentication.logout(userId, history, authHelper.onUserNotAuthenticated));
                        }}
                    >
                        Login
                    </Button>
                </DialogActions>
            </Dialog>
        
    ) : (
        <>
            <Dialog
                disableEscapeKeyDown={true}
                open={props.show}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
                maxWidth="xs">
                <DialogTitle id="form-dialog-title">{t('CHANGE_PASSWORD.PASSWORD')}</DialogTitle>
                <DialogContent>
                    <>
                        <div>
                            <Typography variant="body2" component="p" style={{ marginBottom: 50 }}>
                                {t('CHANGE_PASSWORD.PASSWORD_INFO')}
                            </Typography>
                            <Divider style={{ margin: '30px 0px' }} />

                            {changePassword.progress === ChangePasswordDialogProgress.Failed && (
                                <div style={{ marginBottom: 10 }} data-testid="fail-body">
                                    <div style={{ float: 'left', marginRight: 5 }}>
                                        <ErrorIcon color="error" />
                                    </div>
                                    <Typography variant="body2" color="error" display="inline">
                                        <b> {t('CHANGE_PASSWORD.PROBLEM_OCCURRED')}</b>{' '}
                                    </Typography>
                                    <Typography variant="caption" display="inline">
                                        {t('CHANGE_PASSWORD.ERROR_MESSAGR')}
                                    </Typography>
                                </div>
                            )}

                            <FormControl variant={'filled'} fullWidth style={{ marginBottom: 20 }}>
                                <InputLabel id={'new-label'} htmlFor={'old-password'}>
                                    {t('CHANGE_PASSWORD.OLD_PASSWORD')}
                                </InputLabel>
                                <FilledInput
                                    id={'old-password'}
                                    type={showPassword ? 'text' : 'password'}
                                    value={oldPassword}
                                    inputProps={{ maxLength: 16, 'data-testid': 'old-password' }}
                                    fullWidth
                                    onChange={(e): void => {
                                        setOldPassword(e.target.value);
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={t('FORMS.TOGGLE_PASSWORD_VISIBILITY')}
                                                onClick={(): void => setShowPassword(!showPassword)}
                                                size="large">
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                            <SetPasswordControl
                                passwordLabel={t('LABELS.NEW_PASSWORD')}
                                passwordValidate={(value: boolean): void => setPasswordsValid(value)}
                                onPasswordChange={(newPass: string): void => setNewPassword(newPass)}
                            />
                        </div>
                    </>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'space-between' }}>
                    <Button id="btnBack" data-testid="back" variant="outlined" color="primary" onClick={handleClose}>
                        {t('ACTIONS.BACK')}
                    </Button>
                    <Button
                        id="btnDone"
                        disabled={!passwordValid}
                        data-testid="done"
                        variant="contained"
                        color="primary"
                        onClick={Done}
                    >
                        {t('ACTIONS.DONE')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
